// src/services/moedaService.js
//
// Camada de dados da VitaCoin no app.
//
// NÃO EXISTE LISTENER AQUI, e isso é deliberado. Um `onSnapshot` em
// PACIENTE/{uid}/VITA/ROOT para manter o saldo do TopBar vivo pareceria a
// solução óbvia e seria uma leitura por sessão aberta, por dispositivo, o dia
// inteiro — foi assim que o cache warmer chegou a 20,7M leituras/dia. O saldo
// entra por três caminhos baratos:
//
//   1. localStorage, para o TopBar já abrir com um número (pode estar velho);
//   2. uma chamada de `getCarteiraMoeda` quando a Início monta;
//   3. o `moeda` que as próprias ações já devolvem (check-in, refeição, scan) —
//      o servidor acabou de calcular o saldo, não há motivo para perguntar de novo.
//
// O servidor é o dono do número. Isto aqui é espelho.

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

const CHAVE_CACHE = 'vitacoin:carteira';

// ── store mínimo ─────────────────────────────────────────────────────────────
// Sem Context de propósito: o saldo é lido pelo TopBar, pela Carteira e pela
// Loja, que não compartilham árvore. Um Context teria de envolver o app todo
// para re-renderizar três folhas.

let estado = lerCache();
const inscritos = new Set();

function lerCache() {
  try {
    const cru = localStorage.getItem(CHAVE_CACHE);
    return cru ? JSON.parse(cru) : null;
  } catch {
    return null;
  }
}

function gravarCache(v) {
  try {
    // `ganhoPendente` é estado de UMA sessão (a contagem que a Início ainda
    // vai mostrar); persistido, reanimaria um ganho velho a cada abertura.
    const { ganhoPendente, ...persistivel } = v || {};
    localStorage.setItem(CHAVE_CACHE, JSON.stringify(persistivel));
  } catch {
    /* modo privado, cota cheia: o saldo só não abre instantâneo */
  }
}

function publicar(novo) {
  estado = novo;
  gravarCache(novo);
  inscritos.forEach((fn) => {
    try {
      fn(novo);
    } catch {
      /* um assinante quebrado não derruba os outros */
    }
  });
}

export function assinarMoeda(fn) {
  inscritos.add(fn);
  if (estado) fn(estado);
  return () => inscritos.delete(fn);
}

export function saldoEmCache() {
  return estado;
}

// ── servidor ─────────────────────────────────────────────────────────────────

export async function carregarCarteira() {
  const fn = httpsCallable(functions, 'getCarteiraMoeda');
  const { data } = await fn();
  // Preserva a contagem pendente: a Início monta, dispara esta carga e o
  // CoinBadge ainda vai animar o ganho — sobrescrever aqui a apagaria.
  const ganho = estado?.ganhoPendente || null;
  if (data?.habilitado) publicar({ ...data, ganhoPendente: ganho });
  else publicar({ habilitado: false, saldo: 0 });
  return data;
}

export async function carregarCatalogo() {
  const fn = httpsCallable(functions, 'getCatalogoMoeda');
  const { data } = await fn();
  return data;
}

export async function carregarExtrato({ cursor = null, limite = 20 } = {}) {
  const fn = httpsCallable(functions, 'listarExtratoMoeda');
  const { data } = await fn({ cursor, limite });
  return data;
}

/**
 * Compra um item. A chave de idempotência nasce AQUI, no cliente, e viaja com
 * a chamada: é o que faz o toque duplo no botão e o retry de rede debitarem
 * uma vez só. Sem ela o servidor não teria como saber que é a mesma compra.
 */
export async function comprarItem(itemId, variante = null) {
  const chave = novaChave();
  const fn = httpsCallable(functions, 'comprarItemMoeda');
  const { data } = await fn({ itemId, variante, idempotencyKey: chave });
  if (data?.saldo != null && estado) {
    publicar({ ...estado, saldo: data.saldo });
  }
  return data;
}

function novaChave() {
  try {
    return crypto.randomUUID();
  } catch {
    return `c_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Aplica o `moeda` que uma ação devolveu (check-in, refeição, body scan).
 *
 * Devolve o delta para a animação do CoinBadge, que mostra base e bônus
 * separados — é o momento em que o multiplicador de ofensiva se explica
 * sozinho: "+15 e mais 3 pela sua ofensiva" diz mais que "+18".
 */
export function aplicarGanho(moeda) {
  if (!moeda || !moeda.creditou) return null;
  const valor = Number(moeda.valorFinal) || 0;
  const base = Number(moeda.valorBase) || 0;
  const saldo = Number(moeda.saldoApos);
  const saldoAntes = Number(estado?.saldo) || 0;
  const saldoNovo = Number.isFinite(saldo) ? saldo : saldoAntes + valor;

  publicar({
    ...(estado || { habilitado: true }),
    habilitado: true,
    saldo: saldoNovo,
    // O CoinBadge da tela para onde a pessoa volta conta DE → PARA e mostra o
    // "+N". Fica pendente até alguém consumir (ver consumirGanhoPendente).
    ganhoPendente: valor > 0
      ? { de: saldoAntes, para: saldoNovo, total: valor, id: Date.now() }
      : null,
  });

  return {
    total: valor,
    base,
    bonus: valor - base,
    multiplicador: Number(moeda.multiplicador) || 1,
  };
}

/** O ganho que ainda não foi mostrado contando em tela (ou null). */
export function ganhoPendente() {
  return estado?.ganhoPendente || null;
}

/** A contagem foi exibida: não anima de novo na próxima montagem. */
export function consumirGanhoPendente(id) {
  if (!estado?.ganhoPendente) return;
  if (id != null && estado.ganhoPendente.id !== id) return;
  publicar({ ...estado, ganhoPendente: null });
}

/** Rótulo legível de uma linha do extrato. */
export function rotuloDaOrigem(t, item) {
  if (item.tipo === 'SPEND') {
    return t('moedas:extrato.compra', { item: t(`moedas:item.${item.item}.nome`, {
      defaultValue: item.item || '',
    }) });
  }
  if (item.origem === 'ESTORNO') return t('moedas:extrato.estorno');
  if (item.origem === 'AJUSTE_MANUAL') return t('moedas:extrato.ajuste');
  return t(`moedas:origem.${item.origem}`, { defaultValue: item.origem || '' });
}
